import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { type AdministrativeRegionView } from '@vello/api-client';
import { Button, Field, FormStack, Notice, Row, TextInput } from '@vello/web-theme';
import { REGION_NAME_MAX, normalizeRegionName } from '../../src/index';
import { useT } from '../i18n/provider';
import { FormDialog } from './form-dialog';

const NAME_ID = 'rgn-rename-name';

export interface RegionRenameDialogProps {
  /** The state being renamed; the dialog is open while there is one. */
  readonly region: AdministrativeRegionView | undefined;
  /** A save is in flight: the control is held and the scrim / Escape do not close. */
  readonly busy: boolean;
  /** The translated refusal of the last attempt; the dialog stays open to correct or retry. */
  readonly error: string | undefined;
  readonly onCancel: () => void;
  readonly onConfirm: (name: string) => void;
}

/**
 * Rename a state (ADR-0124, the regions console): one text field, one primary action. The field starts with the current name; the action is held until the
 * name is one the server will take (1 to 120 characters, no line breaks) and differs from the current one. Composed from the shared `FormDialog` shell, so
 * focus, Escape, the scrim and the 48 px controls behave like every other form dialog of the console.
 */
export function RegionRenameDialog({ region, busy, error, onCancel, onConfirm }: RegionRenameDialogProps): ReactElement | null {
  const { t } = useT();
  const [draft, setDraft] = useState('');
  const code = region?.code;
  useEffect(() => {
    setDraft(region?.name ?? '');
  }, [code, region?.name]);

  if (region === undefined) return null;
  const name = normalizeRegionName(draft);
  const invalid = name === undefined;
  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (busy || name === undefined || name === region.name) return;
    onConfirm(name);
  };

  return (
    <FormDialog open id="rgn-rename" icon="pencil" title={t('rgn.rename.title', { name: region.name })} body={t('rgn.rename.body')} busy={busy} initialFocusId={NAME_ID} onCancel={onCancel}>
      <FormStack gap={4} onSubmit={submit} noValidate>
        <Field label={t('rgn.rename.field')} htmlFor={NAME_ID} hint={invalid ? t('rgn.rename.invalid') : t('rgn.rename.hint')}>
          <TextInput id={NAME_ID} value={draft} maxLength={REGION_NAME_MAX} aria-invalid={invalid} disabled={busy} onChange={(e) => setDraft(e.target.value)} />
        </Field>
        {error !== undefined && (
          <div>
            <Notice tone="danger">{error}</Notice>
          </div>
        )}
        <Row gap={2} align="stretch">
          <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={onCancel} disabled={busy}>
            {t('ui.cancel')}
          </Button>
          <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" disabled={busy || name === undefined || name === region.name}>
            {busy ? t('rgn.rename.busy') : t('rgn.rename.confirm')}
          </Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
